/**
 * Phase 19 — Duplicate Payment Protection & Idempotency Engine
 * Guards against double-clicks, browser refreshes, network retries, and duplicate payment instructions.
 */

import { PaymentRecord } from '@/types/domain/payment-management';
import { roundCurrency } from '@/features/payments/utils/paymentCalculations';

export interface DuplicatePaymentResult {
  isDuplicate: boolean;
  reason?: string;
  existingPaymentId?: string;
  existingPaymentRef?: string;
}

/**
 * Screens a proposed payment against the repository of existing payment disbursements.
 */
export function detectDuplicatePayment(
  candidate: {
    id?: string;
    invoiceId: string;
    amount: number;
    paymentReference?: string;
  },
  existingPayments: PaymentRecord[]
): DuplicatePaymentResult {
  if (!candidate.invoiceId || candidate.amount <= 0) {
    return { isDuplicate: false };
  }

  const roundedCandidateAmount = roundCurrency(candidate.amount);

  for (const existing of existingPayments) {
    // Skip self when updating
    if (existing.id === candidate.id) continue;

    // Skip cancelled or permanently failed payments
    if (existing.status === 'CANCELLED' || existing.status === 'FAILED') continue;

    // 1. Check same invoice + same amount in processing or scheduled state
    if (
      existing.invoiceId === candidate.invoiceId &&
      Math.abs(roundCurrency(existing.amount) - roundedCandidateAmount) < 0.01
    ) {
      if (
        existing.status === 'SCHEDULED' ||
        existing.status === 'PROCESSING' ||
        existing.status === 'READY_FOR_PAYMENT'
      ) {
        return {
          isDuplicate: true,
          reason: `Duplicate payment detected: An identical disbursement of ${candidate.amount} is currently ${existing.status} under payment reference #${existing.paymentReference}.`,
          existingPaymentId: existing.id,
          existingPaymentRef: existing.paymentReference,
        };
      }
    }

    // 2. Check identical payment reference
    if (
      candidate.paymentReference &&
      existing.paymentReference.toUpperCase() === candidate.paymentReference.toUpperCase()
    ) {
      return {
        isDuplicate: true,
        reason: `Payment reference #${candidate.paymentReference} has already been issued in ProcureAI.`,
        existingPaymentId: existing.id,
        existingPaymentRef: existing.paymentReference,
      };
    }
  }

  return { isDuplicate: false };
}
